import { useEffect, useState } from "react";
import { Frown, PartyPopper } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../stores/auth";
import { api } from "../lib/api";
import { Button, Spinner } from "../components/ui";

/** Landing page after Stripe/Gkash redirect. Polls the order until the webhook confirms payment. */
export function CheckoutResult() {
  const { moduleEnabled } = useAuth();
  const collectionOn = moduleEnabled("collection");
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const groupId = params.get("group");
  const cancelled = params.get("status") === "cancelled";
  const [status, setStatus] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (groupId && !orderId) {
      setStatus("PAID");
      return;
    }
    if (!orderId || cancelled) return;
    let stopped = false;
    const poll = async () => {
      try {
        const res = await api<{ order: { status: string } }>(`/orders/mine/${orderId}`);
        if (stopped) return;
        setStatus(res.order.status);
        if (res.order.status === "PENDING_PAYMENT" && attempts < 12) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        }
      } catch {
        /* keep last status */
      }
    };
    void poll();
    return () => {
      stopped = true;
    };
  }, [orderId, groupId, cancelled, attempts]);

  if (cancelled) {
    return (
      <div className="flex flex-col items-center px-6 pt-20 text-center">
        <Frown size={56} strokeWidth={1.25} className="text-white/40" />
        <h1 className="mt-4 font-display text-lg font-bold">Payment cancelled</h1>
        <p className="mt-2 text-xs text-white/50">Your order is still reserved — you can retry from the order page.</p>
        <Link to={orderId ? `/orders/${orderId}` : "/orders"} className="mt-6">
          <Button>View order</Button>
        </Link>
      </div>
    );
  }

  if (!status || status === "PENDING_PAYMENT") {
    return (
      <div className="flex flex-col items-center px-6 pt-20 text-center">
        <Spinner className="h-8 w-8" />
        <p className="mt-4 text-xs text-white/50">Confirming your payment…</p>
        {attempts >= 12 && (
          <>
            <p className="mt-2 max-w-64 text-[11px] text-white/40">
              Taking longer than usual — we'll email you as soon as it confirms.
            </p>
            <Link to={orderId ? `/orders/${orderId}` : "/orders"} className="mt-4">
              <Button variant="outline">View order</Button>
            </Link>
          </>
        )}
      </div>
    );
  }

  if (status === "CANCELLED") {
    return (
      <div className="flex flex-col items-center px-6 pt-20 text-center">
        <Frown size={56} strokeWidth={1.25} className="text-white/40" />
        <h1 className="mt-4 font-display text-lg font-bold">Payment didn't go through</h1>
        <Link to="/orders" className="mt-6">
          <Button variant="outline">My orders</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center px-6 pt-20 text-center">
      <PartyPopper size={56} strokeWidth={1.25} className="text-brand-pink" />
      <h1 className="mt-4 font-display text-lg font-bold text-gradient">Payment confirmed!</h1>
      <p className="mt-2 text-xs text-white/50">Thanks for your order — we're on it.</p>
      <div className="mt-6 flex gap-2">
        <Link to={orderId ? `/orders/${orderId}` : "/orders"}>
          <Button>Track order</Button>
        </Link>
        {collectionOn && (
          <Link to="/inventory">
            <Button variant="outline">My inventory</Button>
          </Link>
        )}
      </div>
    </div>
  );
}
